import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  TrophyIcon,
  PlusIcon,
  LayersIcon,
  CalendarIcon,
  UsersIcon,
  HashIcon,
  ClockIcon,
  CalculatorIcon,
  AwardIcon,
  TrashIcon
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const CompetitionRounds = () => {
  const { t } = useTranslation();
  const { isRTL, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const { competitionId } = useParams();

  // State
  const [competition, setCompetition] = useState(null);
  const [rounds, setRounds] = useState([]);
  const [savedRounds, setSavedRounds] = useState([]); // الجولات المحفوظة في الباك آند
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [learningLevels, setLearningLevels] = useState([]);
  const [learningLevelsLoading, setLearningLevelsLoading] = useState(false);

  // New round state
  const [newRound, setNewRound] = useState({
    roundId: 1,
    totalNumbers: 5,
    digitsPerNumber: 2,
    displayTimeMs: 2000,
    operationType: 'addition',
    difficulty: 'easy',
    score: 0,
    levelId: ''
  });

  // Fetch competition details
  const fetchCompetitionDetails = async () => {
    try {
      const response = await api.get(`/competitions/${competitionId}`);
      
      if (response.data.success) {
        setCompetition(response.data.data);
        const existingRounds = response.data.data.rounds || [];
        setSavedRounds(existingRounds);
        setRounds(existingRounds);
        // Set next round ID
        const nextId = existingRounds.length > 0 
          ? Math.max(...existingRounds.map(r => r.roundId)) + 1 
          : 1;
        setNewRound(prev => ({ ...prev, roundId: nextId }));
      }
    } catch (error) {
      console.error('Error fetching competition details:', error);
      toast.error(t('competitions.fetchError'), { duration: 3000, position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  const fetchLearningLevels = async () => {
    setLearningLevelsLoading(true);
    try {
      const response = await api.get('/organization/learning-levels?page=1&limit=100');
      if (response.data.success) {
        setLearningLevels(response.data.data.learningLevels || []);
      }
    } catch (error) {
      console.error('Error fetching learning levels:', error);
    } finally {
      setLearningLevelsLoading(false);
    }
  };

  useEffect(() => {
    fetchCompetitionDetails();
  }, [competitionId]);

  useEffect(() => {
    fetchLearningLevels();
  }, []);

  // Open add modal
  const openAddModal = () => {
    setShowAddModal(true);
  };

  // Close add modal
  const closeAddModal = () => {
    setShowAddModal(false);
    const nextId = rounds.length > 0 
      ? Math.max(...rounds.map(r => r.roundId)) + 1 
      : 1;
    setNewRound({
      roundId: nextId,
      totalNumbers: 5,
      digitsPerNumber: 2,
      displayTimeMs: 2000,
      operationType: 'addition',
      difficulty: 'easy',
      score: 0,
      levelId: ''
    });
  };

  // Add round locally (not saved yet)
  const addRoundLocally = () => {
    // التحقق من صحة البيانات
    if (!newRound.totalNumbers || !newRound.digitsPerNumber || !newRound.displayTimeMs || !newRound.difficulty || newRound.score === '') {
      toast.error(t('competitions.roundDataRequired'), { duration: 3000, position: 'top-center' });
      return;
    }

    // إضافة الجولة محلياً
    const updatedRounds = [...rounds, { ...newRound }];
    setRounds(updatedRounds);
    
    toast.success(t('competitions.roundAddedLocally'), { duration: 2000, position: 'top-center' });
    closeAddModal();
  };

  // Save all rounds to backend
  const saveAllRounds = async () => {
    if (rounds.length === 0) {
      toast.error(t('competitions.noRoundsToSave'), { duration: 3000, position: 'top-center' });
      return;
    }

    setIsSaving(true);
    try {
      // Ensure all rounds have difficulty and levelId
      const roundsWithDifficulty = rounds.map(round => ({
        roundId: round.roundId,
        totalNumbers: round.totalNumbers,
        digitsPerNumber: round.digitsPerNumber,
        displayTimeMs: round.displayTimeMs,
        operationType: round.operationType,
        difficulty: round.difficulty || 'medium',
        score: round.score,
        ...(round.levelId ? { levelId: parseInt(round.levelId) } : {})
      }));

      const response = await api.post(`/competitions/${competitionId}/rounds`, {
        rounds: roundsWithDifficulty
      });
      
      if (response.data.success) {
        toast.success(response.data.message || t('competitions.roundsSaveSuccess'), {
          duration: 3000,
          position: 'top-center'
        });
        setSavedRounds(rounds);
        fetchCompetitionDetails();
      }
    } catch (error) {
      console.error('Error saving rounds:', error);
      toast.error(error.response?.data?.message || t('competitions.roundsSaveError'), {
        duration: 3000,
        position: 'top-center'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Delete round locally
  const deleteRoundLocally = (roundId) => {
    const updatedRounds = rounds.filter(r => r.roundId !== roundId);
    setRounds(updatedRounds);
    toast.success(t('competitions.roundDeletedLocally'), { duration: 2000, position: 'top-center' });
  };

  // Get operation type label
  const getOperationLabel = (type) => {
    const types = {
      addition: t('competitions.addition'),
      subtraction: t('competitions.subtraction'),
      multiplication: t('competitions.multiplication'),
      division: t('competitions.division'),
      mixed: t('competitions.mixed')
    };
    return types[type] || type;
  };

  // Get difficulty label (via competitions translator)
  const getDifficultyLabel = (difficulty) => {
    const difficulties = {
      easy: t('competitions.difficultyEasy'),
      medium: t('competitions.difficultyMedium'),
      hard: t('competitions.difficultyHard')
    };
    return difficulties[difficulty] || difficulty;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className={`p-6 ${isRTL ? 'font-arabic' : ''}`}>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-800 mb-4 transition-colors"
        >
          <ArrowLeftIcon className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
          {t('common.back')}
        </button>

        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <LayersIcon className={`h-8 w-8 text-primary-600 ${isRTL ? 'ml-3' : 'mr-3'}`} />
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <h1 className="text-2xl font-bold text-gray-900">
                {t('competitions.manageRounds')}
              </h1>
              <p className="text-gray-600 mt-1">
                {competition?.name || t('common.loading')}
              </p>
            </div>
          </div>
          
          <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''} gap-3`}>
            {rounds.length !== savedRounds.length && (
              <button
                onClick={saveAllRounds}
                disabled={isSaving}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {isSaving ? t('common.saving') : t('competitions.saveAllRounds')}
              </button>
            )}
            <button
              onClick={openAddModal}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <PlusIcon className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              {t('competitions.addRound')}
            </button>
          </div>
        </div>
      </div>

      {/* Competition Info Cards */}
      {competition && (
        <div className="mb-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Description Card */}
          <div className="bg-white border-l-4 border-primary-500 shadow-md rounded-lg p-5">
            <div className="flex items-center mb-4">
              <div className="bg-primary-100 p-2.5 rounded-lg">
                <TrophyIcon className="h-5 w-5 text-primary-600" />
              </div>
              <h3 className={`text-base font-bold text-gray-800 ${isRTL ? 'mr-3' : 'ml-3'}`}>
                {t('competitions.description')}
              </h3>
            </div>
            <div className={`${isRTL ? 'text-right' : 'text-left'} space-y-2`}>
              <div>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('competitions.competitionTitle')}</span>
                <p className="text-sm font-bold text-gray-900 mt-1">{competition.name}</p>
              </div>
              <div>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('competitions.description')}</span>
                <p className="text-sm text-gray-700 leading-relaxed mt-1">
                  {competition.description || '-'}
                </p>
              </div>
            </div>
          </div>
          
          {/* Statistics Card */}
          <div className="bg-white border-l-4 border-green-500 shadow-md rounded-lg p-5">
            <div className="flex items-center mb-4">
              <div className="bg-green-100 p-2.5 rounded-lg">
                <UsersIcon className="h-5 w-5 text-green-600" />
              </div>
              <h3 className={`text-base font-bold text-gray-800 ${isRTL ? 'mr-3' : 'ml-3'}`}>
                {t('competitions.statistics')}
              </h3>
            </div>
            <div className={`space-y-3 ${isRTL ? 'text-right' : 'text-left'}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{t('competitions.totalParticipants')}</span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-primary-100 text-primary-700">
                  {competition.stats?.totalParticipants || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{t('competitions.rounds')}</span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-blue-100 text-blue-700">
                  {competition.totalRounds}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{t('competitions.currentRounds')}</span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-orange-100 text-orange-700">
                  {rounds.length}
                </span>
              </div>
            </div>
          </div>
          
          {/* Schedule Card */}
          <div className="bg-white border-l-4 border-blue-500 shadow-md rounded-lg p-5">
            <div className="flex items-center mb-4">
              <div className="bg-blue-100 p-2.5 rounded-lg">
                <CalendarIcon className="h-5 w-5 text-blue-600" />
              </div>
              <h3 className={`text-base font-bold text-gray-800 ${isRTL ? 'mr-3' : 'ml-3'}`}>
                {t('competitions.schedule')}
              </h3>
            </div>
            <div className={`space-y-3 ${isRTL ? 'text-right' : 'text-left'}`}>
              <div className="bg-green-50 border-l-2 border-green-400 px-3 py-2.5 rounded">
                <div className="text-xs text-green-700 font-semibold mb-1.5 uppercase tracking-wide">
                  {t('competitions.competitionStart')}
                </div>
                <div className="text-sm font-bold text-green-900">
                  📅 {new Date(competition.schedule?.competitionStart).toLocaleDateString(currentLanguage === 'ar' ? 'ar-SA' : currentLanguage === 'fr' ? 'fr-FR' : 'en-US', { 
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                  })} 🕐 {new Date(competition.schedule?.competitionStart).toLocaleTimeString(currentLanguage === 'ar' ? 'ar-SA' : currentLanguage === 'fr' ? 'fr-FR' : 'en-US', { 
                    hour12: false,
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
              <div className="bg-red-50 border-l-2 border-red-400 px-3 py-2.5 rounded">
                <div className="text-xs text-red-700 font-semibold mb-1.5 uppercase tracking-wide">
                  {t('competitions.competitionEnd')}
                </div>
                <div className="text-sm font-bold text-red-900">
                  📅 {new Date(competition.schedule?.competitionEnd).toLocaleDateString(currentLanguage === 'ar' ? 'ar-SA' : currentLanguage === 'fr' ? 'fr-FR' : 'en-US', { 
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                  })} 🕐 {new Date(competition.schedule?.competitionEnd).toLocaleTimeString(currentLanguage === 'ar' ? 'ar-SA' : currentLanguage === 'fr' ? 'fr-FR' : 'en-US', { 
                    hour12: false,
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rounds Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {rounds.length === 0 ? (
          <div className="text-center py-12">
            <LayersIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('competitions.noRounds')}</h3>
            <p className="mt-1 text-sm text-gray-500">{t('competitions.noRoundsDesc')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('competitions.roundNumber')}
                  </th>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('competitions.operationType')}
                  </th>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('competitions.totalNumbers')}
                  </th>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('competitions.digitsPerNumber')}
                  </th>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('competitions.displayTime')}
                  </th>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('competitions.difficulty')}
                  </th>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('competitions.roundLevel')}
                  </th>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('competitions.score')}
                  </th>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('common.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rounds.map((round, index) => (
                  <tr key={`round-${round.roundId}-${index}`} className="hover:bg-gray-50">
                    <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-primary-100 text-primary-700">
                        #{round.roundId}
                      </span>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        round.operationType === 'addition' ? 'bg-green-100 text-green-800' :
                        round.operationType === 'subtraction' ? 'bg-red-100 text-red-800' :
                        round.operationType === 'multiplication' ? 'bg-blue-100 text-blue-800' :
                        round.operationType === 'division' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-purple-100 text-purple-800'
                      }`}>
                        {getOperationLabel(round.operationType)}
                      </span>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      <div className="text-sm text-gray-900 text-center">{round.totalNumbers}</div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      <div className="text-sm text-gray-900 text-center">{round.digitsPerNumber}</div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      <div className="text-sm text-gray-900 text-center">{round.displayTimeMs} ms</div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        round.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                        round.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        round.difficulty === 'hard' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {getDifficultyLabel(round.difficulty)}
                      </span>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      {round.levelId ? (() => {
                        const level = learningLevels.find(l => l.id === round.levelId || l.id === parseInt(round.levelId));
                        if (!level) return <span className="text-sm text-gray-700">{round.levelId}</span>;
                        const key = `learningLevels.names.${level.id}`;
                        const translated = t(key);
                        const label = translated !== key ? translated : (level.name_ar || level.name || `Level ${level.level_number ?? level.id}`);
                        return <span className="text-sm text-gray-700">{label}</span>;
                      })() : <span className="text-sm text-gray-400">-</span>}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-yellow-100 text-yellow-800">
                        {round.score}
                      </span>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      <button
                        onClick={() => deleteRoundLocally(round.roundId)}
                        className="text-red-600 hover:text-red-900 p-1"
                        title={t('common.delete')}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Round Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${isRTL ? 'text-right' : 'text-left'}`}>
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                {t('competitions.addRound')}
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Round Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('competitions.roundNumber')}
                  </label>
                  <input
                    type="number"
                    value={newRound.roundId}
                    onChange={(e) => setNewRound({ ...newRound, roundId: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    min="1"
                  />
                </div>

                {/* Operation Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('competitions.operationType')}
                  </label>
                  <select
                    value={newRound.operationType}
                    onChange={(e) => setNewRound({ ...newRound, operationType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="addition">{t('competitions.addition')}</option>
                    <option value="subtraction">{t('competitions.subtraction')}</option>
                    <option value="multiplication">{t('competitions.multiplication')}</option>
                    <option value="division">{t('competitions.division')}</option>
                    <option value="mixed">{t('competitions.mixed')}</option>
                  </select>
                </div>

                {/* Total Numbers */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('competitions.totalNumbers')}
                  </label>
                  <input
                    type="number"
                    value={newRound.totalNumbers}
                    onChange={(e) => setNewRound({ ...newRound, totalNumbers: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    min="1"
                  />
                </div>

                {/* Digits Per Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('competitions.digitsPerNumber')}
                  </label>
                  <input
                    type="number"
                    value={newRound.digitsPerNumber}
                    onChange={(e) => setNewRound({ ...newRound, digitsPerNumber: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    min="1"
                  />
                </div>

                {/* Display Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('competitions.displayTime')} (ms)
                  </label>
                  <input
                    type="number"
                    value={newRound.displayTimeMs}
                    onChange={(e) => setNewRound({ ...newRound, displayTimeMs: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    min="500"
                    step="100"
                  />
                </div>

                {/* Difficulty */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('competitions.difficulty')}
                  </label>
                  <select
                    value={newRound.difficulty}
                    onChange={(e) => setNewRound({ ...newRound, difficulty: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="easy">{t('competitions.difficultyEasy')}</option>
                    <option value="medium">{t('competitions.difficultyMedium')}</option>
                    <option value="hard">{t('competitions.difficultyHard')}</option>
                  </select>
                </div>

                {/* Score */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('competitions.score')}
                  </label>
                  <input
                    type="number"
                    value={newRound.score}
                    onChange={(e) => setNewRound({ ...newRound, score: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    min="0"
                    step="0.5"
                  />
                </div>

                {/* Level */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('competitions.roundLevel')}
                  </label>
                  <select
                    value={newRound.levelId}
                    onChange={(e) => setNewRound({ ...newRound, levelId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    disabled={learningLevelsLoading}
                  >
                    <option value="">{learningLevelsLoading ? t('users.loadingLevels') : t('competitions.selectRoundLevel')}</option>
                    {learningLevels.map((level) => {
                      const key = `learningLevels.names.${level.id}`;
                      const translated = t(key);
                      const label = translated !== key ? translated : (level.name_ar || level.name || `Level ${level.level_number ?? level.id}`);
                      return (
                        <option key={level.id} value={level.id}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>

              <div className={`flex ${isRTL ? 'justify-start flex-row-reverse space-x-reverse' : 'justify-end'} space-x-3 mt-6`}>
                <button
                  type="button"
                  onClick={closeAddModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={addRoundLocally}
                  disabled={isAdding}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  {t('common.add')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompetitionRounds;

